import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { checkAuth, type AuthUser } from "./useAuth";
import { redirectToLogin } from "./token";

const CurrentUserContext = createContext<AuthUser | null>(null);

/** The signed-in user, anywhere under RequireAuth. Null outside it. */
export function useCurrentUser(): AuthUser | null {
  return useContext(CurrentUserContext);
}

const apiBase = (): string => (import.meta.env.VITE_API_URL ?? "") as string;

type AuthState =
  | { status: "loading" }
  | { status: "authed"; user: AuthUser }
  | { status: "anon" };

/** Subscription statuses that indicate a lapsed subscription. */
const LAPSED_STATUSES = new Set(["past_due", "canceled", "unpaid"]);

/** Route guard for the BFF cookie session. Verifies the session against
 *  /auth/me; while that's in flight it shows a spinner, on success it renders
 *  the page, and on failure it hands off to the backend /auth/login (full
 *  navigation through Keycloak). There is no token in JS to inspect. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    checkAuth(apiBase()).then((user) => {
      if (cancelled) return;
      if (user) setState({ status: "authed", user });
      else setState({ status: "anon" });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state.status === "anon") redirectToLogin(apiBase());
  }, [state.status]);

  const location = useLocation();
  if (state.status === "authed") {
    const { user } = state;

    // Onboarding gate: user hasn't completed initial setup
    if (user.needs_onboarding && !location.pathname.startsWith("/onboard")) {
      return <Navigate to="/onboard" replace />;
    }

    // Lapsed subscription gate: user completed onboarding but subscription
    // is inactive. Redirect to /reactivate unless already there or in onboarding.
    if (
      !user.needs_onboarding &&
      user.subscription_status &&
      LAPSED_STATUSES.has(user.subscription_status) &&
      !location.pathname.startsWith("/reactivate") &&
      !location.pathname.startsWith("/onboard")
    ) {
      return <Navigate to="/reactivate" replace />;
    }

    return (
      <CurrentUserContext.Provider value={user}>
        {children}
      </CurrentUserContext.Provider>
    );
  }

  // loading or mid-redirect: a neutral full-screen spinner.
  return (
    <Box sx={{ display: "grid", placeItems: "center", height: "100vh" }}>
      <CircularProgress />
    </Box>
  );
}
